import { Shift, UserRole } from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import React, { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { ShiftBonusView } from 'shared/components/shift/bonus'
import { SHIFT_NOTE_HEIGHT, ShiftNoteView } from 'shared/components/shift/note'
import { ShiftPositionView } from 'shared/components/shift/position'
import { ShiftTimeRangeView } from 'shared/components/shift/time_range'
import { ShiftTotalHoursView } from 'shared/components/shift/total_hours'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { ShiftViewProps } from '../types'

export function StoreScheduleShift({
  shift,
  role,
  onShiftDetails,
}: ShiftViewProps) {
  const styles = useStyles(shift);
  
  const handlePress = useCallback(() => {
    onShiftDetails(shift);
  }, [onShiftDetails, shift]);

  const hasInterests = !!(shift.open && shift.interests);
  
  const userDisplayName = useMemo(() => {
    return shift.open
      ? `NEXT\n${t('shift')}`
      : `${shift.user!.firstName!}\n${shift.user!.lastName!}`;
  }, [shift.open, shift.user]);

  const noteMaxWidth = useMemo(() => getShiftNoteMaxWidth(shift), [shift]);

  return (
    <PressableOpacity
      disabled={!onlineManager.isOnline()}
      style={styles.container}
      onPress={handlePress}
    >
      <View style={styles.user}>
        <Text style={styles.name} numberOfLines={3}>
          {userDisplayName}
        </Text>
        <ShiftTotalHoursView hours={shift.user?.totalScheduledHours} />
      </View>
      <UserAvatar
        user={shift.user}
        size={styles.avatar.width}
        hasInterests={hasInterests}
      />
      <View style={styles.shift}>
        <View style={{ flexDirection: 'row', flex: 1 }}>
          <View style={{ flex: 1 }}>
            <ShiftPositionView position={shift.position} />
            <ShiftTimeRangeView shift={shift} showDuration />
          </View>
          {hasInterests ? (
            <View style={styles.interestCountContainer}>
              <Text style={styles.interestCountText}>{shift.interests}</Text>
            </View>
          ) : null}
        </View>
        <View style={styles.noteBonus}>
          {role !== 'helper' && (
            <ShiftNoteView
              note={shift.note}
              maxWidth={noteMaxWidth}
            />
          )}
          <ShiftBonusView shift={shift} />
        </View>
      </View>
    </PressableOpacity>
  )
}

const HEIGHT = 100

export function getStoreScheduleShiftHeight(role: UserRole, shift: Shift) {
  return (
    HEIGHT +
    (!!shift.bonus?.amount || (role !== 'helper' && shift.note)
      ? SHIFT_NOTE_HEIGHT
      : 0)
  )
}

const NOTE_MAX_WIDTH = '80%'

export function getShiftNoteMaxWidth(shift: Shift) {
  return shift.bonus?.amount ? NOTE_MAX_WIDTH : undefined
}

const useStyles = makeStyles((theme, shift: Shift) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    paddingLeft: 5,
  },
  interestCountContainer: {
    alignSelf: 'flex-start',
    justifyContent: 'center',
    width: 25,
    height: 25,
    backgroundColor: theme.colors.red,
    borderRadius: 15,
  },
  interestCountText: {
    textAlign: 'center',
    textAlignVertical: 'center',
    color: 'white',
  },
  user: {
    justifyContent: 'space-between',
    width: 110,
    height: '100%',
    marginRight: 5,
  },
  name: {
    fontSize: 13,
    color: theme.colors?.black,
  },
  shift: {
    flex: 1,
    marginLeft: 10,
    justifyContent: 'space-between',
    height: '100%',
  },
  avatar: {
    width: 50,
  },
  startTime: {
    fontSize: 15,
    color: theme.colors?.black,
  },
  endTime: {
    fontSize: 15,
    color: theme.colors?.grey2,
  },
  noteBonus: {
    borderRadius: 3,
    backgroundColor:
      shift.critical || shift.note || shift.bonus
        ? theme.colors.red
        : 'transparent',
    flexDirection: 'row',
  },
}))
